import type { Reader } from '@/api/client';
import { sortReaderCatalogPrices } from '@/utils/formatNumber';
import { readerCatalogTableDisplayName } from '@/utils/readerCatalogTableDisplayName';
import { normalizeReaderCooperationState } from '@/utils/readerCooperationStates';
import type { FilterRule } from './filterRuleTypes';
import { matchNumber, matchText } from './primitives';

function readerPriceNumbers(reader: Reader): number[] {
  const list = sortReaderCatalogPrices(reader.reader_prices);
  const nums: number[] = [];
  for (const p of list) {
    const n = Number(p.price);
    if (Number.isFinite(n)) nums.push(n);
  }
  return nums;
}

function matchReaderPrices(rule: Extract<FilterRule, { type: 'number' }>, reader: Reader): boolean {
  const nums = readerPriceNumbers(reader);
  const op = rule.op;
  if (op === 'isEmpty') return nums.length === 0;
  if (op === 'isNotEmpty') return nums.length > 0;
  if (op === 'none') return true;
  if (nums.length === 0) return false;
  return nums.some((n) => matchNumber(n, rule));
}

function matchReaderTagsFacet(reader: Reader, rule: Extract<FilterRule, { type: 'readerTagsFacet' }>): boolean {
  const tags = reader.tags ?? [];
  for (const [group, ids] of Object.entries(rule.facets)) {
    if (!ids?.length) continue;
    const idSet = new Set(ids);
    const hit = tags.some((t) => t.tag_group === group && idSet.has(t.id));
    if (!hit) return false;
  }
  return true;
}

export function readersCatalogRowMatchesRule(reader: Reader, columnId: string, rule: FilterRule): boolean {
  if (rule.type === 'readerTagsFacet') {
    return columnId === 'tags' ? matchReaderTagsFacet(reader, rule) : true;
  }
  if (columnId === 'tags') return true;

  switch (columnId) {
    case 'state': {
      const r = rule as Extract<FilterRule, { type: 'select' }>;
      if (!r.values.length) return true;
      const normalized = normalizeReaderCooperationState(reader.cooperation_state);
      return r.values.some((v) => normalizeReaderCooperationState(v) === normalized);
    }
    case 'name':
      return matchText(readerCatalogTableDisplayName(reader), rule as Extract<FilterRule, { type: 'text' }>);
    case 'studio':
      return matchText(reader.studio?.name?.trim() || null, rule as Extract<FilterRule, { type: 'text' }>);
    case 'legal_entity':
      return matchText(
        reader.legal_entity?.name?.trim() || null,
        rule as Extract<FilterRule, { type: 'text' }>
      );
    case 'reader_price':
      return matchReaderPrices(rule as Extract<FilterRule, { type: 'number' }>, reader);
    default:
      return true;
  }
}
